import type { Condition, Patient } from '@medplum/fhirtypes'
import { z } from 'zod'
import { toDateString } from '#src/dates.ts'
import { extractElationId } from '#src/elation.ts'
import { invariant } from '#src/invariant.ts'
import { ConditionClinicalStatus } from '#src/models/conditions/constants.ts'
import { Diagnosis } from '#src/models/conditions/Diagnosis.ts'
import { Participant } from '#src/models/patients/participant.ts'
import { createReference } from '#src/references.ts'
import { recordInvert } from '#src/utils.ts'

export const AllowedStatuses = {
  ACTIVE: 'Active',
  CONTROLLED: 'Controlled',
  RESOLVED: 'Resolved',
} as const

export const DxSchema = z.object({
  icd9: z.array(z.string()),
  icd10: z.array(z.string()),
  snomed: z.string(),
})

export type ProblemDx = z.infer<typeof DxSchema>

export const problemSchema = z.object({
  id: z.number().nullable().optional(),
  description: z.string(),
  status: z.enum([
    AllowedStatuses.ACTIVE,
    AllowedStatuses.CONTROLLED,
    AllowedStatuses.RESOLVED,
  ]),
  start_date: z.string(),
  synopsis: z.string().nullable().optional(),
  resolved_date: z.string().nullable().optional(),
  dx: z.array(DxSchema),
  patient: z.number(),
  rank: z.number().int().optional().nullable(),
  created_date: z.string().nullable().optional(),
  deleted_date: z.string().nullable().optional(),
})

export type Problem = z.infer<typeof problemSchema>

export const fromElationStatus = {
  Active: ConditionClinicalStatus.ACTIVE,
  Controlled: ConditionClinicalStatus.CONTROLLED,
  Resolved: ConditionClinicalStatus.RESOLVED,
}

export const toElationStatus = recordInvert(fromElationStatus)

export function fromFhir(
  condition: Condition,
  patient: Patient | Participant
): Problem {
  const diagnosis = Diagnosis.fromFhirResource(condition)
  const elationPatientId =
    patient instanceof Participant
      ? patient.elationId
      : extractElationId(patient)
  invariant(elationPatientId, 'Patient ID is required')

  const dx: Problem['dx'] = [
    {
      icd10: diagnosis.icd10Codes,
      icd9: diagnosis.icd9Codes,
      snomed: diagnosis.snomedCodes?.[0],
    },
  ]

  let start_date = ''
  if (diagnosis.onsetDate) {
    start_date = toDateString(diagnosis.onsetDate)
  }

  const status =
    toElationStatus[diagnosis.clinicalStatus as keyof typeof toElationStatus]
  invariant(
    status,
    `Unsupported condition clinical status: ${diagnosis.clinicalStatus}`
  )

  return {
    id: diagnosis.elationId,
    description: diagnosis.title,
    status,
    start_date,
    synopsis: diagnosis.note || null,
    dx,
    patient: elationPatientId,
    rank: diagnosis.elationRank,
  }
}

export function toFhir(
  problem: Problem,
  patient: Patient | Participant
): Condition {
  const clinicalStatus = fromElationStatus[problem.status]
  const diagnosis = new Diagnosis({
    subject:
      patient instanceof Participant
        ? patient.reference
        : createReference(patient),
    clinicalStatus,
    title: problem.description,
    note: problem.synopsis ?? undefined,
    recordedDate: problem.created_date
      ? new Date(problem.created_date)
      : undefined,
  })
  diagnosis.onsetDate = new Date(problem.start_date)
  diagnosis.icd10Codes = problem.dx.flatMap((dx) => dx.icd10)
  diagnosis.icd9Codes = problem.dx.flatMap((dx) => dx.icd9)
  diagnosis.snomedCodes = problem.dx.map((dx) => ({ code: dx.snomed }))
  diagnosis.elationId = problem.id ?? undefined
  diagnosis.elationRank = problem.rank ?? undefined
  diagnosis.externalResourceJSON = JSON.stringify(problem)

  return diagnosis.toFhirResource()
}
